import React, { useState } from 'react';
import { LightBox, Drawer } from '@lion-design/core';
import { useSelector, useDispatch } from 'react-redux';
import '@magaele/ic-ln/css.css';
import styles from './styles.module.scss';
import { useMediaQuery } from 'react-responsive';
import cN from 'classnames';
import { RootState } from '@Redux/search/store';
import { BarChart } from '@features/chart/BarChart';
import { RTOW_SINGLE, RTOW_RETURN } from '@utils/utils';
import dayjs from 'dayjs';
import { use } from 'i18next';
import OutOfRangeDate from '../OutOfRangeDateLightBox';
import { setSearchInfoCalendarBar } from '@Redux/search/actions';

interface I_RateChartLightBoxProps {
    isOpen: boolean;
    handleTrendModalClose: () => void;
}

const BAR_HEIGHT_MAX_PRICE = 0.7;
const BAR_HEIGHT_NO_DATA = 0.4;
const BAR_HEIGHT_LOADING = 0.15;

const MAX_DAYS_DIFF = 360;
const MIN_DAYS_DIFF = 1;


const RateChartLightBox = ({ isOpen, handleTrendModalClose }: I_RateChartLightBoxProps) => {
    const { SearchInfoCalendarBar, UrlParams } = useSelector((state: RootState) => state.search);
    const dispatch = useDispatch();
    const isLoadingBar = SearchInfoCalendarBar.isLoading;
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const departDate1 = UrlParams.SeekDestinations[0]?.DepartDate;
    const departDate2 = UrlParams.SeekDestinations[1]?.DepartDate;
    const [daysDiff, setDaysDiff] = useState(() => {
        if (departDate1 && departDate2) {
            return dayjs(departDate2).diff(dayjs(departDate1), 'day');
        }
        return 0;
    });

    const data = SearchInfoCalendarBar.data;
    const dataList = [];
    const priceMap = new Map();
    let maxPrice = 0;
    for (let i = 0; i < data.FlightInfos.length; i++) {
        const price = data.FlightInfos[i].FareInfos[0].AdtPrice;
        maxPrice = Math.max(maxPrice, price);
        const date = data.FlightInfos[i].ItineraryInfos[0].DepDateTime;
        priceMap.set(dayjs(date).format('MM/DD'), price);
    }
    if (maxPrice === 0) {
        // 如果14個日期都沒有從API拿到價格，則給他一個預設數值，用於計算長條圖高度
        maxPrice = 100;
    }

    const formattedSelectedDepartDate = dayjs(UrlParams.SeekDestinations[0].DepartDate);
    const formattedSelectedReturnDate = UrlParams.Rtow === RTOW_RETURN ? formattedSelectedDepartDate.add(daysDiff, "day") : null;
    const firstDepartDate = formattedSelectedDepartDate.subtract(6, 'day');
    const firstReturnDate = formattedSelectedReturnDate ? formattedSelectedReturnDate.subtract(6, 'day') : null;
    const labelsList = [];
    for (let i = 0; i < 14; i++) {
        const label = [firstDepartDate.add(i, 'day').format('MM/DD')];
        if (isLoadingBar) {
            dataList.push(maxPrice * BAR_HEIGHT_LOADING / BAR_HEIGHT_MAX_PRICE);
        } else if (priceMap.has(label[0])) {
            dataList.push(priceMap.get(label[0]));
        } else {
            dataList.push(maxPrice * BAR_HEIGHT_NO_DATA / BAR_HEIGHT_MAX_PRICE);
        }
        if (firstReturnDate) {
            label.push(firstReturnDate.add(i, 'day').format('MM/DD'));
        }
        labelsList.push(label);
    }


    //以最高價來反推Y軸高度
    const yAxisHeight = maxPrice / BAR_HEIGHT_MAX_PRICE;
    //loading的bar高度
    const loadingBarHeight = maxPrice * BAR_HEIGHT_LOADING / BAR_HEIGHT_MAX_PRICE;

    //最遠可訂購日
    const maxBookingDate = dayjs().add(MAX_DAYS_DIFF, 'day');
    //這裡用predict，以解決按加號時，會多一天才彈出alert的問題
    const predictedReturnDate = formattedSelectedDepartDate.add(daysDiff + 1, 'day');
    //加減日期天數
    const handleDaysIncrement = () => {
        if (isLoadingBar)
            return;
        if (predictedReturnDate.isAfter(maxBookingDate)) {
            setOpenAlertModal(true);
        } else {
            // 如果没有超出最大日期，則加一天
            setDaysDiff(prevDays => Math.min(prevDays + 1, MAX_DAYS_DIFF));
            const urlParamsClone = JSON.parse(JSON.stringify(UrlParams));
            urlParamsClone.SeekDestinations[1].DepartDate = formattedSelectedDepartDate.add(daysDiff + 1, 'day').format('YYYY-MM-DD');
            dispatch(setSearchInfoCalendarBar(urlParamsClone));
        }
    };

    const handleDaysDecrement = () => {
        if (isLoadingBar)
            return;
        if (daysDiff === MIN_DAYS_DIFF) {
            setOpenAlertModal(true);
        } else {
            // 如果没有超出最小日期，則減一天
            setDaysDiff(prevDays => Math.max(prevDays - 1, MIN_DAYS_DIFF));
            const urlParamsClone = JSON.parse(JSON.stringify(UrlParams));
            urlParamsClone.SeekDestinations[1].DepartDate = formattedSelectedDepartDate.add(daysDiff - 1, 'day').format('YYYY-MM-DD');
            dispatch(setSearchInfoCalendarBar(urlParamsClone));
        }
    };


    const closeModal = () => {
        setOpenAlertModal(false)
    }

    const formatDate = (date: dayjs.Dayjs): string => {
        const month = (date.month() + 1).toString().padStart(2, '0');
        const day = date.date().toString().padStart(2, '0');
        return `${month}月${day}日`;
    };


    const handleOnClick = (event: MouseEvent, array: any) => {
        if (isLoadingBar)
            return;
        if (array.length > 0) {
            let departDate = array[0]._model.label[0].replace("/", "-");
            let returnDate = UrlParams.Rtow === RTOW_RETURN ? array[0]._model.label[1].replace("/", "-") : departDate;
            const year = parseInt(departDate1.substring(0, 4));
            if (returnDate >= departDate) {
                departDate = year + "-" + departDate;
                returnDate = year + "-" + returnDate;
            } else {
                departDate = year + "-" + departDate;
                returnDate = (year + 1) + "-" + returnDate;
            }
            const queryParts = location.search.slice(1).split('&');
            // 遍歷並修改 DepDate1 和 DepDate2
            const updatedParts = queryParts.map(part => {
                if (part.startsWith('DepDate1=')) {
                    return 'DepDate1=' + departDate; // 修改 DepDate1
                }
                if (part.startsWith('DepDate2=')) {
                    return 'DepDate2=' + returnDate; // 修改 DepDate2
                }
                return part; // 其他部分保持不變
            });

            // 將修改後的參數重新組合成查詢字串
            const updatedUrl = `?${updatedParts.join('&')}`;
            window.location.replace(updatedUrl);
        }
    }

    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });


    return isMobile ? (
        <Drawer className={styles.customDrawer} placement='bottom' closable={true} visible={isOpen} onClose={() => handleTrendModalClose()} >
            <div className={styles.mobileContainer}>
                <div className={styles.mobileContent}>
                    <div className={styles.mobileTitle}>票價趨勢</div>
                    <div className={styles.mobileSubtitle}>
                        {UrlParams.Rtow === RTOW_SINGLE &&
                            <div className={styles.mobileDates}>出發日期：<span className={styles.mobileDate}>{formatDate(formattedSelectedDepartDate)}</span></div>
                        }
                        {UrlParams.Rtow === RTOW_RETURN &&
                            <>
                                <div className={styles.mobileDates}>出發日期：<span className={styles.mobileDate}>{formatDate(formattedSelectedDepartDate)}</span>，回程日期：<span className={styles.mobileDate}>{formatDate(formattedSelectedDepartDate.add(daysDiff, 'day'))}</span>&emsp;</div>
                                <div className={styles.mobileDaysCounter}>|&emsp;旅遊天數
                                    <span className={cN('ic-ln toolcancels2', styles.icon, (isLoadingBar || daysDiff <= MIN_DAYS_DIFF) && styles.disabledIcon)} onClick={handleDaysDecrement} />
                                    <span className={styles.daysDiff}>{daysDiff + 1}</span>
                                    <span className={cN('ic-ln tooladds2', styles.icon, (isLoadingBar || predictedReturnDate.isAfter(maxBookingDate)) && styles.disabledIcon)} onClick={handleDaysIncrement} />
                                </div>
                            </>
                        }
                    </div>
                    {openAlertModal && (
                        <OutOfRangeDate openAlertModal={openAlertModal} closeModal={closeModal} />
                    )}
                    <div className={styles.notice}>左右滑動可查看更多票價</div>
                </div>
                <div>
                    <BarChart labelsList={labelsList} dataList={dataList} yAxisHeight={yAxisHeight} loadingBarHeight={loadingBarHeight} handleOnClick={handleOnClick} priceMap={priceMap} isLoadingBar={isLoadingBar} />
                    <div className={styles.remark}>備註：機票價格變動頻繁，顯示每位成人最低價格，僅供參考。幣別為TWD。</div>
                </div>
            </div>
        </Drawer >

    ) :
        (
            <LightBox className={styles.rateChartLightBox} open={isOpen} ToggleLightBox={() => handleTrendModalClose()}>
                <div className={styles.chartTitle}>
                    <div className={styles.title}>票價趨勢</div>
                    <div className={styles.subtitle}>
                        {UrlParams.Rtow === RTOW_SINGLE && <div>出發日期：<span className={styles.date}>{formatDate(formattedSelectedDepartDate)}</span></div>}
                        {UrlParams.Rtow === RTOW_RETURN &&
                            <><div>出發日期：<span className={styles.date}>{formatDate(formattedSelectedDepartDate)}</span>，回程日期：<span className={styles.date}>{formatDate(formattedSelectedDepartDate.add(daysDiff, 'day'))}</span>&emsp;</div>
                                <div className={styles.daysCounter}>|&emsp;旅遊天數
                                    <span className={cN('ic-ln toolcancels2', styles.icon, (isLoadingBar || daysDiff <= MIN_DAYS_DIFF) && styles.disabledIcon)} onClick={handleDaysDecrement} />
                                    <span className={styles.daysDiff}>{daysDiff + 1}</span>
                                    <span className={cN('ic-ln tooladds2', styles.icon, (isLoadingBar || predictedReturnDate.isAfter(maxBookingDate)) && styles.disabledIcon)} onClick={handleDaysIncrement} />
                                </div>
                            </>
                        }
                    </div>
                </div>
                {openAlertModal && (
                    <OutOfRangeDate openAlertModal={openAlertModal} closeModal={closeModal} />
                )}
                <div>
                    <BarChart labelsList={labelsList} dataList={dataList} yAxisHeight={yAxisHeight} loadingBarHeight={loadingBarHeight} handleOnClick={handleOnClick} priceMap={priceMap} isLoadingBar={isLoadingBar} />
                    <div className={styles.remark}>備註：機票價格變動頻繁，顯示每位成人最低價格，僅供參考。幣別為TWD。</div>
                </div>
            </LightBox >

        )
};

export default RateChartLightBox;
